import { GAME_NAME } from '@geosport/shared';
import { useEffect, type ReactNode } from 'react';
import { Link } from '../lib/router';

interface Item {
  to: string;
  label: string;
  hint?: string;
  /** A server-rendered content page (full page load, not an in-app route). */
  page?: boolean;
}

const SECTIONS: { title: string; items: Item[] }[] = [
  {
    title: 'Play',
    items: [
      { to: '/', label: 'Today’s round' },
      { to: '/leaderboard', label: 'Leaderboard' },
    ],
  },
  {
    title: 'Your game',
    items: [
      { to: '/stats', label: 'Stats' },
      { to: '/groups', label: 'Groups', hint: 'Private leaderboards with friends' },
      { to: '/practice', label: 'Practice', hint: 'Random rounds and previous days' },
    ],
  },
  {
    title: 'Explore',
    items: [
      { to: '/puzzles', label: 'Archive', hint: 'Past puzzles with answers', page: true },
      { to: '/sports', label: 'Sports quizzes', hint: 'Questions by sport', page: true },
      { to: '/compare', label: 'Similar games', page: true },
    ],
  },
  {
    title: 'About',
    items: [
      { to: '/how-to-play', label: 'How it works' },
      { to: '/faq', label: 'FAQ', page: true },
      { to: '/privacy', label: 'Privacy' },
      { to: '/terms', label: 'Terms' },
    ],
  },
];

const ITEM_CLASS = 'flex min-h-11 flex-col justify-center rounded-lg px-2 py-1.5 hover:bg-white/10';

/** Slide-in navigation (left). `extra` renders below the sections (settings, install). */
export function Drawer({
  open,
  onClose,
  extra,
}: {
  open: boolean;
  onClose: () => void;
  extra?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex bg-black/60" onClick={onClose}>
      <nav
        aria-label="Main menu"
        className="animate-fade-up flex h-full w-[min(20rem,85vw)] flex-col overflow-y-auto bg-ink-2 p-4 pt-[max(1rem,env(safe-area-inset-top))] shadow-2xl ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <Link to="/" onClick={onClose} className="flex items-center gap-2 text-lg font-bold">
            <img src="/favicon.svg" alt="" className="h-7 w-7" />
            {GAME_NAME}
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10"
          >
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {SECTIONS.map((s) => (
          <section key={s.title} className="mb-4">
            <h2 className="mb-1 px-2 text-[11px] font-bold tracking-[0.15em] text-white/40 uppercase">
              {s.title}
            </h2>
            <ul>
              {s.items.map((it) => {
                const label = (
                  <>
                    <span className="font-semibold">{it.label}</span>
                    {it.hint && <span className="text-xs text-white/50">{it.hint}</span>}
                  </>
                );
                return (
                  <li key={it.to}>
                    {it.page ? (
                      <a href={it.to} className={ITEM_CLASS}>
                        {label}
                      </a>
                    ) : (
                      <Link to={it.to} onClick={onClose} className={ITEM_CLASS}>
                        {label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {extra}
      </nav>
    </div>
  );
}
